import React, { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable } from "../ask/reportAsk.utils";
import {
  formatDate,
  formatHours,
  formatMoney,
  unbilledByClient,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { PeopleClientTypeFilters, useReportFilters } from "../ui/ReportFilters";
import {
  Avatar,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllTime } from "../useReportData";

export const UnbilledReport: FC = () => {
  const navigate = useNavigate();
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const time = useAllTime({
    billed: false,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    workTypeIds: filters.workTypeIds,
  });
  const rows = useMemo(() => unbilledByClient(time.data), [time.data]);
  const hours = rows.reduce((s, r) => s + r.hours, 0);
  const value = rows.reduce((s, r) => s + r.value, 0);
  const entries = rows.reduce((s, r) => s + r.entries, 0);
  const oldest = rows.reduce<typeof rows[number] | null>(
    (o, r) =>
      r.ageDays !== null && (!o || (o.ageDays ?? -1) < r.ageDays) ? r : o,
    null,
  );
  const v = (x: React.ReactNode) => (time.isLoading ? "…" : x);

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        Client: r.name,
        Entries: r.entries,
        Hours: Math.round(r.hours * 100) / 100,
        Value: Math.round(r.value),
        "Oldest entry": formatDate(r.oldest),
        "Age (days)": r.ageDays ?? "",
      })),
      "Unbilled time",
    );

  const shownRows = useFocusRows("Unbilled time by client", rows, (r) => r.key);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Unbilled time",
      path: "/reports/unbilled",
      description: "Billable time not on an invoice yet, by client.",
      kpis: [
        { label: "Unbilled time", value: formatHours(hours) },
        { label: "Unbilled value", value: formatMoney(value) },
        { label: "Clients", value: String(rows.length) },
      ],
      tables: [
        askTable(
          "Unbilled time by client",
          rows,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "entries",
              label: "Entries",
              kind: "number",
              value: (r) => r.entries,
            },
            {
              key: "hours",
              label: "Hours",
              kind: "hours",
              value: (r) => r.hours,
            },
            {
              key: "value",
              label: "Value",
              kind: "money",
              value: (r) => r.value,
            },
            {
              key: "oldest",
              label: "Oldest entry",
              kind: "date",
              value: (r) => formatDate(r.oldest),
            },
            {
              key: "age",
              label: "Age (days)",
              kind: "number",
              value: (r) => r.ageDays,
            },
          ],
          (r) => r.key,
          (r) =>
            r.customerId
              ? `/billing/invoices/add?customerId=${r.customerId}`
              : null,
        ),
      ],
    }),
    [rows, hours, value],
  );

  return (
    <ReportPage
      title={"Unbilled time (WIP)"}
      description={
        "Time that hasn't been put on an invoice yet, by client. Create the invoice straight from here."
      }
      onExport={rows.length ? onExport : undefined}
      ask={time.isLoading ? undefined : ask}
      askSuggestions={[
        "Who should we invoice first?",
        "Anything older than 60 days?",
        "Which clients have the most unbilled value?",
      ]}
      filters={
        <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
      }>
      {time.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Unbilled time"}
          value={v(formatHours(hours))}
          note={`${entries} time entries`}
        />
        <Kpi
          label={"Unbilled value"}
          value={v(formatMoney(value))}
          note={"At each person's hourly rate"}
        />
        <Kpi
          label={"Clients"}
          value={v(rows.length)}
          note={"With unbilled time"}
        />
        <Kpi
          label={"Oldest entry"}
          value={v(oldest?.ageDays != null ? `${oldest.ageDays} days` : "—")}
          note={
            oldest ? `${oldest.name} · ${formatDate(oldest.oldest)}` : undefined
          }
          tone={"red"}
        />
      </KpiGrid>

      <Card title={"Unbilled time by client"}>
        {time.isLoading ? (
          <Loading />
        ) : time.isError ? null : !rows.length ? (
          <Empty text={"All tracked time is on an invoice."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Entries
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Hours
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Value
                </th>
                <th scope={"col"} className={TH}>
                  Oldest entry
                </th>
                <th scope={"col"} className={TH}>
                  <span className={"sr-only"}>Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr key={r.key}>
                  <td className={`${TD} font-bold`}>
                    <Avatar name={r.name} square />
                    {r.name}
                  </td>
                  <td className={`${TD} ${NUM}`}>{r.entries}</td>
                  <td className={`${TD} ${NUM}`}>{formatHours(r.hours)}</td>
                  <td className={`${TD} ${NUM} font-extrabold`}>
                    {formatMoney(r.value)}
                  </td>
                  <td className={TD}>
                    {formatDate(r.oldest)}
                    {r.ageDays !== null && (
                      <span
                        className={`ml-1.5 font-extrabold ${
                          r.ageDays >= 30
                            ? "text-[#B42318]"
                            : r.ageDays >= 14
                            ? "text-[#8A4100]"
                            : "text-[#3F4757]"
                        }`}>
                        · {r.ageDays} days
                      </span>
                    )}
                  </td>
                  <td className={`${TD} ${NUM}`}>
                    {r.customerId && (
                      <button
                        type={"button"}
                        onClick={() =>
                          navigate(
                            `/billing/invoices/add?customerId=${r.customerId}`,
                            {
                              state: {
                                defaultCustomer: {
                                  value: r.customerId,
                                  label: r.name,
                                },
                              },
                            },
                          )
                        }
                        className={
                          "h-[34px] rounded-[9px] border border-[#6B4EFF] bg-white px-3 text-[12.5px] font-bold text-[#4B32D6] hover:bg-[#F0EDFF]"
                        }>
                        Create invoice
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Counts all time that isn&apos;t on an invoice yet. Values use each
        person&apos;s current hourly rate.
      </Note>
    </ReportPage>
  );
};
